import type { ReactNode } from 'react';
import { Badge, Card, Col, ProgressBar, Row } from 'react-bootstrap';

import './BiasAggregateSummaryPanel.css';
import {
  formatMetricThresholdFromEntry,
  metricApiStatusFromEntry,
  metricScalarFromEntry,
  overallRiskTierLabel
} from './biasSummaryReference.utils';

/** Legacy empty KPI placeholder (still treated as “missing” alongside {@link KPI_NA}). */
export const KPI_EMPTY = '—';

/** API `null` / missing scalar KPI display (Bias, Fairness, Explainability tiles). */
export const KPI_NA = 'N/A';

export function isKpiMissingDisplay(s: string): boolean {
  return s === KPI_EMPTY || s === KPI_NA;
}

const ROW_ACCENTS_ROW2_BIAS = ['violet', 'fuchsia', 'rose', 'emerald'] as const;
const ROW_ACCENTS_ROW2_GENERIC = ['violet', 'fuchsia', 'rose', 'emerald'] as const;

function fmtScore3(v: unknown): string {
  if (v == null || v === '') return KPI_NA;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return KPI_NA;
  return n.toFixed(3);
}

/** Fairness API uses -1 when score is unknown / not computed; still show the numeric sentinel. */
function fmtFairnessScore3(v: unknown): string {
  if (v == null || v === '') return KPI_NA;
  const n = Number(v);
  if (!Number.isFinite(n)) return KPI_NA;
  return n.toFixed(3);
}

function fmtInt(v: unknown): string {
  if (v == null || v === '') return KPI_NA;
  const n = Number(v);
  if (!Number.isFinite(n)) return KPI_NA;
  return String(Math.trunc(n));
}

function fmtText(v: unknown): string {
  if (v == null || v === '') return KPI_NA;
  const s = String(v).trim();
  return s.length ? s : KPI_NA;
}

/** Dataset governance verdict: REJECT / REVIEW / APPROVE (Bias & Fairness aggregate_summary). */
function datasetVerdictBadgeBg(raw: unknown): 'danger' | 'warning' | 'success' | 'secondary' {
  const u = String(raw ?? '')
    .trim()
    .toUpperCase();
  if (u === 'REJECT') return 'danger';
  if (u === 'REVIEW') return 'warning';
  if (u === 'APPROVE') return 'success';
  return 'secondary';
}

/** Explainability verdict (may use HIGH / MEDIUM / LOW). */
function governanceVerdictBadgeBg(raw: unknown): 'danger' | 'warning' | 'success' | 'secondary' {
  const u = String(raw ?? '')
    .trim()
    .toUpperCase();
  if (u === 'REJECT' || u === 'HIGH') return 'danger';
  if (u === 'REVIEW' || u === 'MEDIUM' || u === 'MODERATE') return 'warning';
  if (u === 'APPROVE' || u === 'LOW') return 'success';
  return 'secondary';
}

function readAvgMetric(aggregate: Record<string, unknown> | undefined, key: string): unknown {
  const avg = aggregate?.avg_metrics;
  if (!avg || typeof avg !== 'object' || Array.isArray(avg)) return undefined;
  return metricScalarFromEntry((avg as Record<string, unknown>)[key]);
}

function metricStatusBadgeVariant(statusRaw: string): 'success' | 'danger' | 'warning' | 'info' | 'secondary' {
  const s = statusRaw.trim().toLowerCase();
  if (s === 'pass') return 'success';
  if (s === 'fail') return 'danger';
  if (s === 'watch') return 'warning';
  if (s === 'stable') return 'info';
  if (s === 'improved') return 'success';
  if (s === 'degraded') return 'danger';
  return 'secondary';
}

function readMetricEntry(
  aggregate: Record<string, unknown> | undefined,
  key: string,
  source: 'avg_metrics' | 'audit_avg_metrics'
): unknown {
  const block = aggregate?.[source];
  if (!block || typeof block !== 'object' || Array.isArray(block)) return undefined;
  return (block as Record<string, unknown>)[key];
}

function readAvgMetricMeta(
  aggregate: Record<string, unknown> | undefined,
  key: string
): { threshold: string; status: string | null } {
  const avgEntry = readMetricEntry(aggregate, key, 'avg_metrics');
  const auditEntry = readMetricEntry(aggregate, key, 'audit_avg_metrics');

  const prettyThreshold = (entry: unknown): string | null => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
    const root = entry as Record<string, unknown>;
    const t =
      root.threshold && typeof root.threshold === 'object' && !Array.isArray(root.threshold)
        ? (root.threshold as Record<string, unknown>)
        : root;
    const warn = t.warn ?? t.w;
    const fail = t.fail ?? t.f;
    const warnN = Number(warn);
    const failN = Number(fail);
    if (Number.isFinite(warnN) && Number.isFinite(failN)) {
      return `Warn ${warnN} | Fail ${failN}`;
    }
    return formatMetricThresholdFromEntry(entry);
  };

  const avgThreshold = prettyThreshold(avgEntry);
  const threshold = avgThreshold ?? prettyThreshold(auditEntry) ?? KPI_NA;

  const avgStatus = metricApiStatusFromEntry(avgEntry);
  const status = avgStatus ?? metricApiStatusFromEntry(auditEntry);

  return { threshold, status };
}

function readNested(root: unknown, path: string[]): unknown {
  let cur: unknown = root;
  for (const p of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/** Matches legacy bias-dash Risk tier card tones (`BiasAggregateSummaryPanel`). */
function riskTierHeroTone(riskTierLabel: string | null): 'high' | 'medium' | 'low' | 'unknown' {
  const r = (riskTierLabel ?? '').toUpperCase();
  if (r === 'HIGH') return 'high';
  if (r === 'MEDIUM' || r === 'MODERATE') return 'medium';
  if (r === 'LOW') return 'low';
  return 'unknown';
}

function riskTierProgressVariant(
  tone: 'high' | 'medium' | 'low' | 'unknown'
): 'danger' | 'warning' | 'success' | 'secondary' {
  if (tone === 'high') return 'danger';
  if (tone === 'medium') return 'warning';
  if (tone === 'low') return 'success';
  return 'secondary';
}

/** Bias aggregate `risk_score`: valid range is ≥ 0; negatives are not shown as numbers. */
function fmtBiasRiskScore3(v: unknown): string {
  if (v == null || v === '') return KPI_NA;
  const n = Number(v);
  if (!Number.isFinite(n)) return KPI_NA;
  if (n < 0) return KPI_NA;
  return n.toFixed(3);
}

/** Mean of `bias_analysis.bias_risk_score` across LLM prompt rows when aggregate has no top-level risk. */
function meanBiasRiskScoreFromLlmEntries(entries: unknown[] | null | undefined): unknown {
  if (!Array.isArray(entries) || entries.length === 0) return undefined;
  const nums: number[] = [];
  for (const e of entries) {
    if (!e || typeof e !== 'object') continue;
    const ba = (e as Record<string, unknown>).bias_analysis;
    if (!ba || typeof ba !== 'object') continue;
    const o = ba as Record<string, unknown>;
    const n = Number(o.bias_risk_score ?? o.Bias_Risk_Score);
    if (Number.isFinite(n)) nums.push(n);
  }
  if (nums.length === 0) return undefined;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

/** ML aggregate-only shape: approximate 0–1 risk from high-risk attribute share. */
function mlBiasRiskFractionFromCounts(agg: Record<string, unknown>): unknown {
  if (agg.total_attributes == null || agg.high_risk_count == null) return undefined;
  const t = Number(agg.total_attributes);
  const h = Number(agg.high_risk_count);
  if (!Number.isFinite(t) || t <= 0 || !Number.isFinite(h) || h < 0) return undefined;
  return Math.min(1, Math.max(0, h / t));
}

/**
 * API may send `risk_score`, `bias_risk_score`, `Bias_Risk_Score`, `microeai_bias_score` (LLM aggregate),
 * or nest under `Summary`. ML-only blobs may omit scalars; then use high_risk_count/total_attributes or
 * the mean of per-row `bias_analysis.bias_risk_score`.
 */
function biasModuleRiskScoreRaw(
  agg: Record<string, unknown> | undefined,
  llmEntriesForRisk?: unknown[] | null
): unknown {
  if (!agg) return undefined;
  const top =
    agg.risk_score ??
    agg.bias_risk_score ??
    (agg as { Bias_Risk_Score?: unknown }).Bias_Risk_Score ??
    agg.microeai_bias_score;
  if (top != null && top !== '') return top;
  const summary = agg.Summary;
  if (summary && typeof summary === 'object' && !Array.isArray(summary)) {
    const s = summary as Record<string, unknown>;
    const nested =
      s.risk_score ??
      s.bias_risk_score ??
      (s as { Bias_Risk_Score?: unknown }).Bias_Risk_Score ??
      s.microeai_bias_score;
    if (nested != null && nested !== '') return nested;
  }
  const mlFrac = mlBiasRiskFractionFromCounts(agg);
  if (mlFrac != null) return mlFrac;
  const fromEntries = meanBiasRiskScoreFromLlmEntries(llmEntriesForRisk);
  if (fromEntries != null) return fromEntries;
  return undefined;
}

function KpiTile({
  label,
  value,
  accent,
  hint
}: {
  label: string;
  value: ReactNode;
  accent: string;
  hint?: ReactNode;
}) {
  return (
    <div className="bias-dash-tile bias-dash-tile--compact h-100" data-accent={accent}>
      <div className="bias-dash-tile__label">{label}</div>
      <div className="bias-dash-tile__value">{value}</div>
      {hint ? <div className="bias-dash-tile__hint">{hint}</div> : null}
    </div>
  );
}

export function BiasModuleKpiGrid({
  aggregate,
  llmEntriesForRisk
}: {
  aggregate: Record<string, unknown> | null | undefined;
  /** LLM `data` / prompt rows — used to average `bias_analysis.bias_risk_score` when aggregate has no risk scalar. */
  llmEntriesForRisk?: unknown[] | null;
}) {
  const agg = aggregate && typeof aggregate === 'object' ? aggregate : undefined;

  const riskScoreRaw = biasModuleRiskScoreRaw(agg, llmEntriesForRisk);
  const riskScore = fmtBiasRiskScore3(riskScoreRaw);
  const riskScoreNum = Number(riskScoreRaw);
  const riskScoreBarNow =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0
      ? 0
      : Math.min(100, Math.max(0, riskScoreNum * 100));

  const riskTierResolved = overallRiskTierLabel(agg ?? undefined);
  const tierTone = riskTierHeroTone(riskTierResolved);
  const tierVariant = riskTierProgressVariant(tierTone);
  const tierDisplay = riskTierResolved ?? KPI_NA;

  const riskScoreBarVariant =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0 ? 'secondary' : tierVariant;

  const datasetStatus = fmtText(agg?.dataset_status);
  const datasetVerdictRaw = fmtText(agg?.dataset_verdict);
  const verdictBarVariant = datasetVerdictBadgeBg(agg?.dataset_verdict);
  const verdictCardAccent =
    verdictBarVariant === 'danger'
      ? 'bias-dash-verdict-card--danger'
      : verdictBarVariant === 'warning'
      ? 'bias-dash-verdict-card--warning'
      : verdictBarVariant === 'success'
      ? 'bias-dash-verdict-card--success'
      : '';

  const crows = fmtScore3(readAvgMetric(agg, 'CrowsPairs'));
  const stereo = fmtScore3(readAvgMetric(agg, 'StereoSet'));
  const tox = fmtScore3(readAvgMetric(agg, 'Toxicity'));
  const weat = fmtScore3(readAvgMetric(agg, 'WEAT_Avg'));
  const crowsMeta = readAvgMetricMeta(agg, 'CrowsPairs');
  const stereoMeta = readAvgMetricMeta(agg, 'StereoSet');
  const toxMeta = readAvgMetricMeta(agg, 'Toxicity');
  const weatMeta = readAvgMetricMeta(agg, 'WEAT_Avg');

  const row2 = [
    { label: 'Group Favoritism', node: crows, meta: crowsMeta },
    { label: 'Stereotype Score', node: stereo, meta: stereoMeta },
    { label: 'Hostility Score', node: tox, meta: toxMeta },
    { label: 'Association Skew', node: weat, meta: weatMeta }
  ];

  return (
    <div className="bias-dash mb-2" aria-label="Bias module KPIs">
      <div className="bias-dash-verdict-cards">
        <Row className="g-2 g-md-2 mt-1 align-items-stretch">
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent bias-dash-risk-tier-card h-100 flex-fill w-100 border-0 shadow-sm bias-dash-risk-tier-card--${tierTone}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Tier</span>
                <div
                  className={`bias-dash-verdict-card__value text-uppercase bias-dash-risk-tier-card__value--${tierTone}`}
                >
                  {tierDisplay}
                </div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Overall exposure from verdict mix and sensitive-term signals.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(tierDisplay) ? 0 : 100}
                    variant={tierVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Score</span>
                <div className="bias-dash-verdict-card__value">{riskScore}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Aggregate risk on a 0–1 scale (3&nbsp;decimal display).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={riskScoreBarNow}
                    variant={riskScoreBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Dataset Status</span>
                <div className="bias-dash-verdict-card__value text-break">{datasetStatus}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Current dataset processing / readiness state.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(datasetStatus) ? 0 : 100}
                    variant="secondary"
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm ${verdictCardAccent}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Dataset Verdict</span>
                <div className="bias-dash-verdict-card__value text-uppercase">{datasetVerdictRaw}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Governance gate for this dataset (REVIEW / APPROVE / REJECT).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(datasetVerdictRaw) ? 0 : 100}
                    variant={verdictBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <Row className="g-2 mt-2">
        {row2.map((k, i) => (
          <Col key={k.label} xs={12} sm={6} md={3}>
            <KpiTile
              label={k.label}
              value={k.node}
              accent={ROW_ACCENTS_ROW2_BIAS[i]}
              hint={
                <span className="bias-dash-kpi-meta">
                  <span className="bias-dash-kpi-meta__threshold">Threshold: {k.meta.threshold}</span>
                  <span className="d-inline-flex align-items-center gap-1">
                    <span className="bias-dash-kpi-meta__status">Status:</span>
                    {k.meta.status ? (
                      <Badge
                        bg={metricStatusBadgeVariant(k.meta.status)}
                        className="bias-dash-metric__status-badge text-uppercase fw-medium"
                      >
                        {k.meta.status}
                      </Badge>
                    ) : (
                      <span className="bias-dash-kpi-meta__status">{KPI_NA}</span>
                    )}
                  </span>
                </span>
              }
            />
          </Col>
        ))}
      </Row>
    </div>
  );
}

/** Resolve 0–1 fairness risk: API scalars, then ML `high_risk_count` / `total_attributes`. */
function fairnessCoverageBreakdown(agg: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!agg) return undefined;
  const cb =
    (agg.coverage_breakdown as Record<string, unknown> | undefined) ??
    (agg as { Coverage_Breakdown?: Record<string, unknown> }).Coverage_Breakdown;
  if (cb && typeof cb === 'object' && !Array.isArray(cb)) return cb;
  return undefined;
}

function fairnessFairCountRaw(agg: Record<string, unknown> | undefined): unknown {
  if (!agg) return undefined;
  const nested = readNested(agg, ['fairness_summary', 'fair_count']);
  if (nested !== undefined && nested !== null) return nested;
  const fs = agg.fairness_summary as Record<string, unknown> | undefined;
  if (fs && typeof fs === 'object') {
    const fc = fs.Fair_Count ?? fs.fairCount;
    if (fc !== undefined && fc !== null) return fc;
  }
  return agg.fair_count ?? (agg as { Fair_Count?: unknown }).Fair_Count;
}

/** Primary: `aggregate_summary.risk_score`; ML-only runs may omit it → proxy from counts. */
function fairnessModuleRiskScoreRaw(agg: Record<string, unknown> | undefined): unknown {
  if (!agg) return undefined;
  const direct =
    agg.risk_score ?? agg.fairness_risk_score ?? (agg as { Fairness_Risk_Score?: unknown }).Fairness_Risk_Score;
  if (direct != null && direct !== '') {
    const n = Number(direct);
    if (Number.isFinite(n)) return n;
  }
  const t = Number(agg.total_attributes);
  const h = Number(agg.high_risk_count);
  if (Number.isFinite(t) && t > 0 && Number.isFinite(h) && h >= 0) {
    return Math.min(1, Math.max(0, h / t));
  }
  return undefined;
}

/** Primary: `aggregate_summary.risk_tier`; else verdict-derived tier. */
function fairnessRiskTierLabel(agg: Record<string, unknown> | undefined): string | null {
  if (!agg) return null;
  const direct = agg.risk_tier ?? (agg as { Risk_Tier?: unknown }).Risk_Tier ?? agg.overall_risk_tier;
  if (typeof direct === 'string' && direct.trim()) return direct.trim().toUpperCase();
  return overallRiskTierLabel(agg);
}

export function FairnessModuleKpiGrid({ aggregate }: { aggregate: Record<string, unknown> | null | undefined }) {
  const agg = aggregate && typeof aggregate === 'object' ? aggregate : undefined;

  const riskScalar = fairnessModuleRiskScoreRaw(agg);
  const riskScore = fmtBiasRiskScore3(riskScalar);
  const riskScoreNum = Number(riskScalar);
  const riskScoreBarNow =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0
      ? 0
      : Math.min(100, Math.max(0, riskScoreNum * 100));

  const riskTierResolved = fairnessRiskTierLabel(agg);
  const tierTone = riskTierHeroTone(riskTierResolved);
  const tierVariant = riskTierProgressVariant(tierTone);
  const tierDisplay = riskTierResolved ?? KPI_NA;

  const riskScoreBarVariant =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0 ? 'secondary' : tierVariant;

  const datasetStatus = fmtText(agg?.dataset_status ?? agg?.['Dataset_Status'] ?? agg?.overall_risk_status);
  const datasetVerdictSource = agg?.dataset_verdict ?? agg?.['Dataset_Verdict'];
  const datasetVerdictRaw = fmtText(datasetVerdictSource);
  const verdictBarVariant = datasetVerdictBadgeBg(datasetVerdictSource);
  const verdictCardAccent =
    verdictBarVariant === 'danger'
      ? 'bias-dash-verdict-card--danger'
      : verdictBarVariant === 'warning'
      ? 'bias-dash-verdict-card--warning'
      : verdictBarVariant === 'success'
      ? 'bias-dash-verdict-card--success'
      : '';

  const overallFair = fmtFairnessScore3(
    agg?.overall_fairness_score ?? agg?.['Overall_Fairness_Score'] ?? agg?.microeai_fairness_score
  );
  const fairCount = fmtInt(fairnessFairCountRaw(agg));
  const cov = fairnessCoverageBreakdown(agg);
  /** Spec: `coverage_breakdown.ELIGIBLE` */
  const eligible = fmtInt(cov?.ELIGIBLE ?? cov?.eligible ?? cov?.Eligible ?? (cov as { eligible?: unknown })?.eligible);
  /** Spec: `coverage_breakdown.PARTIAL` */
  const partial = fmtInt(cov?.PARTIAL ?? cov?.partial ?? cov?.Partial ?? (cov as { partial?: unknown })?.partial);

  const row2 = [
    { label: 'Overall Fairness', node: overallFair },
    { label: 'Fair Count', node: fairCount },
    { label: 'ELIGIBLE Coverage', node: eligible },
    { label: 'PARTIAL Coverage', node: partial }
  ];

  return (
    <div className="bias-dash mb-2" aria-label="Fairness module KPIs">
      <div className="bias-dash-verdict-cards">
        <Row className="g-2 g-md-2 mt-1 align-items-stretch">
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Score</span>
                <div className="bias-dash-verdict-card__value">{riskScore}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Fairness aggregate risk on a 0–1 scale (3&nbsp;decimal display).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={riskScoreBarNow}
                    variant={riskScoreBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent bias-dash-risk-tier-card h-100 flex-fill w-100 border-0 shadow-sm bias-dash-risk-tier-card--${tierTone}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Tier</span>
                <div
                  className={`bias-dash-verdict-card__value text-uppercase bias-dash-risk-tier-card__value--${tierTone}`}
                >
                  {tierDisplay}
                </div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Uses aggregate risk tier when the API sends it; otherwise derived from verdict signals.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(tierDisplay) ? 0 : 100}
                    variant={tierVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Dataset Status</span>
                <div className="bias-dash-verdict-card__value text-break">{datasetStatus}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Current dataset processing / readiness state.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(datasetStatus) ? 0 : 100}
                    variant="secondary"
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm ${verdictCardAccent}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Dataset Verdict</span>
                <div className="bias-dash-verdict-card__value text-uppercase">{datasetVerdictRaw}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Governance gate for this dataset (REVIEW / APPROVE / REJECT).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(datasetVerdictRaw) ? 0 : 100}
                    variant={verdictBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <Row className="g-2 mt-2">
        {row2.map((k, i) => (
          <Col key={k.label} xs={12} sm={6} md={3}>
            <KpiTile label={k.label} value={k.node} accent={ROW_ACCENTS_ROW2_GENERIC[i]} />
          </Col>
        ))}
      </Row>
    </div>
  );
}

function explainabilityRiskScoreValue(llm: Record<string, unknown>): unknown {
  const direct = llm.risk_score ?? llm.Risk_Score;
  if (direct != null && direct !== '') return direct;
  const me = llm.microeai_explainability as Record<string, unknown> | undefined;
  return me?.score;
}

/**
 * Read `llm_model_explainability.microeai_explainability.drivers.{attribution_risk|instability_risk}`
 * (spec paths) with API aliases. Falls back to top-level `drivers` on the LLM block, then
 * `risk_weights` on nested microeai or block (Workbench-style payloads).
 */
function explainabilityDriverMetric(
  llmBlock: Record<string, unknown> | undefined,
  specSnake: 'attribution_risk' | 'instability_risk'
): unknown {
  if (!llmBlock) return undefined;
  const me = (llmBlock.microeai_explainability ?? llmBlock.Microeai_Explainability) as
    | Record<string, unknown>
    | undefined;
  const keyLists =
    specSnake === 'attribution_risk'
      ? ['attribution_risk', 'Attribution_Risk', 'attributionRisk', 'AttributionRisk', 'attribution', 'Attribution']
      : ['instability_risk', 'Instability_Risk', 'instabilityRisk', 'InstabilityRisk', 'instability', 'Instability'];

  const tryDriversObject = (dr: unknown): unknown => {
    if (!dr || typeof dr !== 'object' || Array.isArray(dr)) return undefined;
    const o = dr as Record<string, unknown>;
    for (const k of keyLists) {
      const v = o[k];
      if (v != null && v !== '') return v;
    }
    for (const [k, v] of Object.entries(o)) {
      const nk = k.toLowerCase().replace(/[^a-z0-9]/g, '');
      const want =
        specSnake === 'attribution_risk' ? ['attributionrisk', 'attribution'] : ['instabilityrisk', 'instability'];
      if (want.some(w => nk.includes(w)) && v != null && v !== '') return v;
    }
    return undefined;
  };

  for (const root of [me, llmBlock]) {
    if (!root) continue;
    const hit = tryDriversObject(root.drivers);
    if (hit !== undefined) return hit;
  }

  for (const root of [me, llmBlock]) {
    if (!root) continue;
    const rw = root.risk_weights as Record<string, unknown> | undefined;
    if (rw && typeof rw === 'object') {
      if (specSnake === 'instability_risk' && rw.instability != null) return rw.instability;
      if (specSnake === 'attribution_risk') {
        if (rw.concentration != null) return rw.concentration;
        if (rw.sensitive != null) return rw.sensitive;
      }
    }
  }
  return undefined;
}

/** Workbench `LLM_Model_Explainability.source_attribution` — pick strongest bucket when no flat `top_attribution_source`. */
function explainabilityTopAttributionFromSource(block: Record<string, unknown>): string {
  const sa = block.source_attribution as Record<string, unknown> | undefined;
  if (!sa || typeof sa !== 'object') return KPI_NA;
  let bestK = '';
  let bestV = -Infinity;
  for (const [k, v] of Object.entries(sa)) {
    const n = Number(v);
    if (Number.isFinite(n) && n > bestV) {
      bestV = n;
      bestK = k;
    }
  }
  if (bestK && bestV > 0) return bestK;
  return KPI_NA;
}

export function ExplainabilityModuleKpiGrid({ llm }: { llm: Record<string, unknown> | null | undefined }) {
  const block = llm && typeof llm === 'object' ? llm : undefined;
  const me = (block?.microeai_explainability ?? (block as Record<string, unknown>)?.Microeai_Explainability) as
    | Record<string, unknown>
    | undefined;
  const lime = block?.lime_stability as Record<string, unknown> | undefined;
  const faith = block?.faithfulness as Record<string, unknown> | undefined;
  const metrics = block?.metrics as Record<string, unknown> | undefined;

  const riskScoreRaw = block ? explainabilityRiskScoreValue(block) : undefined;
  const riskScore = block ? fmtBiasRiskScore3(riskScoreRaw) : KPI_NA;
  const riskScoreNum = Number(riskScoreRaw);
  const riskScoreBarNow =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0
      ? 0
      : Math.min(100, Math.max(0, riskScoreNum * 100));

  const riskTierRaw = block
    ? fmtText(block.risk_tier ?? block.Risk_Tier ?? (me?.verdict as string | undefined))
    : KPI_NA;
  const tierTone = riskTierHeroTone(isKpiMissingDisplay(riskTierRaw) ? null : riskTierRaw);
  const tierVariant = riskTierProgressVariant(tierTone);
  const tierDisplay = riskTierRaw;

  const riskScoreBarVariant =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0 ? 'secondary' : tierVariant;

  const topAttrDirect = block ? fmtText(block.top_attribution_source ?? block.top_Attribution_Source) : KPI_NA;
  const topAttr =
    block && !isKpiMissingDisplay(topAttrDirect)
      ? topAttrDirect
      : block
      ? explainabilityTopAttributionFromSource(block)
      : KPI_NA;

  const verdictRaw = block ? fmtText(block.verdict ?? me?.verdict) : KPI_NA;
  const explVerdictBarVariant = governanceVerdictBadgeBg(verdictRaw);
  const explVerdictCardAccent =
    explVerdictBarVariant === 'danger'
      ? 'bias-dash-verdict-card--danger'
      : explVerdictBarVariant === 'warning'
      ? 'bias-dash-verdict-card--warning'
      : explVerdictBarVariant === 'success'
      ? 'bias-dash-verdict-card--success'
      : '';

  const stability = block
    ? fmtScore3(
        metrics?.stability ??
          metrics?.Stability ??
          lime?.median_std ??
          (lime as { medianStd?: unknown })?.medianStd ??
          block.median_lime_std
      )
    : KPI_NA;

  const faithfulness = block
    ? fmtScore3(
        metrics?.faithfulness ??
          metrics?.Faithfulness ??
          faith?.mean_aopc ??
          (faith as { mean_Aopc?: unknown })?.mean_Aopc ??
          block.mean_aopc
      )
    : KPI_NA;

  const attrRisk = block ? fmtScore3(explainabilityDriverMetric(block, 'attribution_risk')) : KPI_NA;
  const instRisk = block ? fmtScore3(explainabilityDriverMetric(block, 'instability_risk')) : KPI_NA;

  const row2 = [
    { label: 'Stability', node: stability },
    { label: 'Faithfulness', node: faithfulness },
    { label: 'Attribution Risk', node: attrRisk },
    { label: 'Instability Risk', node: instRisk }
  ];

  return (
    <div className="bias-dash mb-2" aria-label="Explainability module KPIs">
      <div className="bias-dash-verdict-cards">
        <Row className="g-2 g-md-2 mt-1 align-items-stretch">
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Score</span>
                <div className="bias-dash-verdict-card__value">{riskScore}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Explainability risk score (XRS-style; 3&nbsp;decimal display).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={riskScoreBarNow}
                    variant={riskScoreBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent bias-dash-risk-tier-card h-100 flex-fill w-100 border-0 shadow-sm bias-dash-risk-tier-card--${tierTone}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Tier</span>
                <div
                  className={`bias-dash-verdict-card__value text-uppercase bias-dash-risk-tier-card__value--${tierTone}`}
                >
                  {tierDisplay}
                </div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Overall exposure tier for this explainability run.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(tierDisplay) ? 0 : 100}
                    variant={tierVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Top Attribution</span>
                <div className="bias-dash-verdict-card__value text-break">{topAttr}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Primary source driving attribution for this run.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(topAttr) ? 0 : 100}
                    variant="secondary"
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm ${explVerdictCardAccent}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Verdict</span>
                <div className="bias-dash-verdict-card__value text-uppercase">{verdictRaw}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Explainability outcome (e.g. HIGH / MEDIUM / LOW).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(verdictRaw) ? 0 : 100}
                    variant={explVerdictBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <Row className="g-2 mt-2">
        {row2.map((k, i) => (
          <Col key={k.label} xs={12} sm={6} md={3}>
            <KpiTile label={k.label} value={k.node} accent={ROW_ACCENTS_ROW2_GENERIC[i]} />
          </Col>
        ))}
      </Row>
    </div>
  );
}
